import { Eye, Info, Search } from "lucide-react";
import { useState } from "react";
import { Button } from "../../ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../ui/dialog";
import { ScrollArea } from "../../ui/scroll-area";
import { Badge } from "../../ui/badge";
import { Input } from "../../ui/input";

interface DetailsDialogProps {
  data: any;
  title?: string;
  trigger?: "default" | "icon";
  testId?: string;
}

function formatKey(key: string): string {
  return key
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (str) => str.toUpperCase())
    .trim();
}

function flattenObject(obj: any, prefix: string = ''): Array<{ key: string; value: any }> {
  const result: Array<{ key: string; value: any }> = [];

  for (const [key, value] of Object.entries(obj)) {
    const formattedKey = formatKey(prefix ? `${prefix}.${key}` : key);

    if (value === null || value === undefined) {
      result.push({ key: formattedKey, value: null });
    } else if (typeof value === 'boolean' || typeof value === 'number' || typeof value === 'string') {
      result.push({ key: formattedKey, value });
    } else if (Array.isArray(value)) {
      if (value.length === 0) {
        result.push({ key: formattedKey, value: [] });
      } else if (typeof value[0] === 'string' || typeof value[0] === 'number') {
        result.push({ key: formattedKey, value });
      } else {
        value.forEach((item, idx) => {
          if (typeof item === 'object' && item !== null) {
            result.push(...flattenObject(item, `${prefix ? prefix + '.' : ''}${key}[${idx}]`));
          } else {
            result.push({ key: `${formattedKey}[${idx}]`, value: item });
          }
        });
      }
    } else if (typeof value === 'object') {
      result.push(...flattenObject(value, prefix ? `${prefix}.${key}` : key));
    } else {
      result.push({ key: formattedKey, value });
    }
  }

  return result;
}

function renderValue(value: any) {
  if (value === null || value === undefined) {
    return <span className="text-sm font-medium">-</span>;
  }

  if (typeof value === 'boolean') {
    return <span className="text-sm font-medium">{value.toString()}</span>;
  }

  if (typeof value === 'number') {
    return <span className="text-sm font-medium">{value}</span>;
  }

  if (typeof value === 'string') {
    return <span className="text-sm font-medium break-all">{value}</span>;
  }

  if (Array.isArray(value)) {
    if (value.length === 0) {
      return <span className="text-sm font-medium">-</span>;
    }

    return (
      <div className="flex flex-wrap gap-1">
        {value.map((item, idx) => (
          <Badge key={idx} variant="outline" className="text-xs border-foreground">
            {String(item)}
          </Badge>
        ))}
      </div>
    );
  }

  return <span className="text-sm font-medium">{String(value)}</span>;
}

export function DetailsDialog({ data, title = "Details", trigger = "default", testId = "button-details" }: DetailsDialogProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const flattenedData = flattenObject(data);

  const filteredData = flattenedData.filter((item) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    const keyMatch = item.key.toLowerCase().includes(query);
    const valueMatch = String(item.value).toLowerCase().includes(query);
    return keyMatch || valueMatch;
  });

  return (
    <Dialog>
      <DialogTrigger asChild>
        {trigger === "icon" ? (
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 flex-shrink-0"
            data-testid={testId}
          >
            <Info className="w-3 h-3" />
          </Button>
        ) : (
          <Button variant="outline" size="sm" className="gap-2" data-testid={testId}>
            <Eye className="w-4 h-4" />
            Details
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-3xl max-h-[80vh]">
        <DialogHeader className="pb-4 border-b">
          <DialogTitle className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-md bg-primary/10 flex items-center justify-center flex-shrink-0">
              <Info className="w-5 h-5 text-primary" />
            </div>
            <span className="text-lg font-semibold">{title}</span>
          </DialogTitle>
        </DialogHeader>

        <div className="relative pt-4">
          <Input
            type="text"
            placeholder="Search fields..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
            data-testid="input-search-details"
          />
          <Search className="absolute left-3 top-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
        </div>

        <ScrollArea className="max-h-[calc(80vh-200px)] pr-4 pt-4">
          <div className="grid grid-cols-2 gap-x-10 gap-y-6">
            {filteredData.map((item, idx) => (
              <div key={idx} className="space-y-2 min-w-0">
                <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{item.key}</div>
                <div className="min-w-0">{renderValue(item.value)}</div>
              </div>
            ))}
          </div>
          {filteredData.length === 0 && (
            <div className="text-center py-8 text-sm text-muted-foreground">
              No results found
            </div>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}